import React from 'react';
import { Link } from 'react-router-dom';
import { Truck, ArrowLeft, Shield, FileText, AlertTriangle, CheckCircle2 } from 'lucide-react';
import { Button } from '../components/ui/button';

export default function UserAgreement() {
  return (
    <div className="min-h-screen bg-slate-50 py-12 px-4">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <Link to="/" className="inline-flex items-center gap-2 mb-6">
            <Truck className="h-8 w-8 text-orange-500" />
            <span className="text-xl font-bold text-slate-900">True Choice Enterprise</span>
          </Link>
          <h1 className="text-3xl font-bold text-slate-900">Carrier &amp; Driver User Agreement</h1>
          <p className="text-slate-600 mt-2">FMCSA Compliance and Platform Terms</p>
          <p className="text-sm text-slate-500 mt-1">Last Updated: January 2025</p>
        </div>

        {/* Agreement Content */}
        <div className="bg-white rounded-2xl shadow-lg border border-slate-200 overflow-hidden">
          {/* Important Notice Banner */}
          <div className="bg-orange-50 border-b border-orange-200 p-6">
            <div className="flex gap-4">
              <AlertTriangle className="h-6 w-6 text-orange-500 flex-shrink-0 mt-1" />
              <div>
                <h2 className="font-bold text-slate-900">Important Notice</h2>
                <p className="text-slate-700 mt-1">
                  By registering as a Carrier or Driver on True Choice Enterprise, you acknowledge and agree to comply 
                  with all applicable Federal Motor Carrier Safety Administration (FMCSA) regulations at all times while 
                  using this platform and conducting any transportation activities.
                </p>
              </div>
            </div>
          </div>

          <div className="p-8 space-y-8">
            {/* Section 1 */}
            <section>
              <h2 className="text-xl font-bold text-slate-900 mb-4 flex items-center gap-2">
                <span className="w-8 h-8 bg-orange-500 text-white rounded-full flex items-center justify-center text-sm font-bold">1</span>
                FMCSA Compliance Requirements
              </h2>
              <div className="bg-slate-50 rounded-xl p-6 space-y-4">
                <p className="text-slate-700">
                  All Carriers and Drivers using True Choice Enterprise must maintain full compliance with the Federal Motor 
                  Carrier Safety Administration (FMCSA) regulations, including but not limited to:
                </p>
                <ul className="space-y-3">
                  <li className="flex gap-3">
                    <CheckCircle2 className="h-5 w-5 text-green-500 flex-shrink-0 mt-0.5" />
                    <span><strong>Operating Authority:</strong> Carriers must maintain valid USDOT numbers and, where required, MC (Motor Carrier) authority.</span>
                  </li>
                  <li className="flex gap-3">
                    <CheckCircle2 className="h-5 w-5 text-green-500 flex-shrink-0 mt-0.5" />
                    <span><strong>Insurance Requirements:</strong> Carriers must maintain minimum levels of liability insurance as required by 49 CFR Part 387.</span>
                  </li>
                  <li className="flex gap-3">
                    <CheckCircle2 className="h-5 w-5 text-green-500 flex-shrink-0 mt-0.5" />
                    <span><strong>Driver Qualifications:</strong> All drivers must meet the qualification requirements under 49 CFR Part 391, including valid CDL credentials where applicable.</span>
                  </li>
                  <li className="flex gap-3">
                    <CheckCircle2 className="h-5 w-5 text-green-500 flex-shrink-0 mt-0.5" />
                    <span><strong>Hours of Service (HOS):</strong> Drivers must comply with all Hours of Service regulations under 49 CFR Part 395.</span>
                  </li>
                  <li className="flex gap-3">
                    <CheckCircle2 className="h-5 w-5 text-green-500 flex-shrink-0 mt-0.5" />
                    <span><strong>Drug &amp; Alcohol Testing:</strong> Carriers and drivers must comply with controlled substances and alcohol testing requirements under 49 CFR Part 382.</span>
                  </li>
                  <li className="flex gap-3">
                    <CheckCircle2 className="h-5 w-5 text-green-500 flex-shrink-0 mt-0.5" />
                    <span><strong>Vehicle Maintenance:</strong> All commercial motor vehicles must be properly maintained and inspected per 49 CFR Parts 393 and 396.</span>
                  </li>
                  <li className="flex gap-3">
                    <CheckCircle2 className="h-5 w-5 text-green-500 flex-shrink-0 mt-0.5" />
                    <span><strong>Electronic Logging Device (ELD):</strong> Carriers and drivers must use compliant ELDs as required by the ELD mandate.</span>
                  </li>
                </ul>
              </div>
            </section>

            {/* Section 2 */}
            <section>
              <h2 className="text-xl font-bold text-slate-900 mb-4 flex items-center gap-2">
                <span className="w-8 h-8 bg-orange-500 text-white rounded-full flex items-center justify-center text-sm font-bold">2</span>
                Carrier Responsibilities
              </h2>
              <div className="bg-slate-50 rounded-xl p-6 space-y-4">
                <p className="text-slate-700">As a registered Carrier on True Choice Enterprise, you agree to:</p>
                <ul className="space-y-2 text-slate-700">
                  <li className="flex gap-2">
                    <span className="text-orange-500 font-bold">•</span>
                    Maintain an active and satisfactory FMCSA safety rating
                  </li>
                  <li className="flex gap-2">
                    <span className="text-orange-500 font-bold">•</span>
                    Keep your USDOT registration and MCS-150 form current
                  </li>
                  <li className="flex gap-2">
                    <span className="text-orange-500 font-bold">•</span>
                    Ensure all employed or contracted drivers meet FMCSA qualification standards
                  </li>
                  <li className="flex gap-2">
                    <span className="text-orange-500 font-bold">•</span>
                    Maintain accurate driver qualification files as required by 49 CFR Part 391
                  </li>
                  <li className="flex gap-2">
                    <span className="text-orange-500 font-bold">•</span>
                    Report any safety violations as required by law
                  </li>
                  <li className="flex gap-2">
                    <span className="text-orange-500 font-bold">•</span>
                    Provide proof of insurance and authority upon request
                  </li>
                </ul>
              </div>
            </section>

            {/* Section 3 */}
            <section>
              <h2 className="text-xl font-bold text-slate-900 mb-4 flex items-center gap-2">
                <span className="w-8 h-8 bg-orange-500 text-white rounded-full flex items-center justify-center text-sm font-bold">3</span>
                Driver Responsibilities
              </h2>
              <div className="bg-slate-50 rounded-xl p-6 space-y-4">
                <p className="text-slate-700">As a registered Driver on True Choice Enterprise, you agree to:</p>
                <ul className="space-y-2 text-slate-700">
                  <li className="flex gap-2">
                    <span className="text-orange-500 font-bold">•</span>
                    Hold and maintain a valid Commercial Driver&apos;s License (CDL) with appropriate endorsements
                  </li>
                  <li className="flex gap-2">
                    <span className="text-orange-500 font-bold">•</span>
                    Comply with all Hours of Service (HOS) regulations
                  </li>
                  <li className="flex gap-2">
                    <span className="text-orange-500 font-bold">•</span>
                    Submit to pre-employment and random drug/alcohol testing as required
                  </li>
                  <li className="flex gap-2">
                    <span className="text-orange-500 font-bold">•</span>
                    Report any medical conditions that may affect your ability to operate a CMV safely
                  </li>
                  <li className="flex gap-2">
                    <span className="text-orange-500 font-bold">•</span>
                    Conduct and document pre-trip and post-trip vehicle inspections
                  </li>
                  <li className="flex gap-2">
                    <span className="text-orange-500 font-bold">•</span>
                    Immediately report any traffic violations, accidents, or license suspensions
                  </li>
                  <li className="flex gap-2">
                    <span className="text-orange-500 font-bold">•</span>
                    Maintain accurate records of duty status (RODS) using an approved ELD
                  </li>
                  <li className="flex gap-2">
                    <span className="text-orange-500 font-bold">•</span>
                    Operate only vehicles you are qualified and licensed to drive
                  </li>
                </ul>
              </div>
            </section>

            {/* Section 4 */}
            <section>
              <h2 className="text-xl font-bold text-slate-900 mb-4 flex items-center gap-2">
                <span className="w-8 h-8 bg-orange-500 text-white rounded-full flex items-center justify-center text-sm font-bold">4</span>
                Prohibited Conduct
              </h2>
              <div className="bg-red-50 border border-red-200 rounded-xl p-6 space-y-4">
                <p className="text-slate-700">
                  The following conduct is strictly prohibited and will result in immediate termination from the platform:
                </p>
                <ul className="space-y-2 text-slate-700">
                  <li className="flex gap-2">
                    <AlertTriangle className="h-5 w-5 text-red-500 flex-shrink-0 mt-0.5" />
                    Operating a commercial motor vehicle while impaired by drugs or alcohol
                  </li>
                  <li className="flex gap-2">
                    <AlertTriangle className="h-5 w-5 text-red-500 flex-shrink-0 mt-0.5" />
                    Falsifying records of duty status, driver qualification files, or other required documents
                  </li>
                  <li className="flex gap-2">
                    <AlertTriangle className="h-5 w-5 text-red-500 flex-shrink-0 mt-0.5" />
                    Operating without valid operating authority or required insurance
                  </li>
                  <li className="flex gap-2">
                    <AlertTriangle className="h-5 w-5 text-red-500 flex-shrink-0 mt-0.5" />
                    Exceeding Hours of Service limits or circumventing ELD requirements
                  </li>
                  <li className="flex gap-2">
                    <AlertTriangle className="h-5 w-5 text-red-500 flex-shrink-0 mt-0.5" />
                    Operating an unsafe or uninspected vehicle
                  </li>
                  <li className="flex gap-2">
                    <AlertTriangle className="h-5 w-5 text-red-500 flex-shrink-0 mt-0.5" />
                    Providing false information during registration
                  </li>
                </ul>
              </div>
            </section>

            {/* Section 5 */}
            <section>
              <h2 className="text-xl font-bold text-slate-900 mb-4 flex items-center gap-2">
                <span className="w-8 h-8 bg-orange-500 text-white rounded-full flex items-center justify-center text-sm font-bold">5</span>
                Indemnification &amp; Liability
              </h2>
              <div className="bg-slate-50 rounded-xl p-6 space-y-4">
                <p className="text-slate-700">
                  Carriers and Drivers agree to indemnify, defend, and hold harmless True Choice Enterprise, its officers, 
                  directors, employees, and agents from any claims, damages, losses, or expenses arising from:
                </p>
                <ul className="space-y-2 text-slate-700">
                  <li className="flex gap-2">
                    <span className="text-orange-500 font-bold">•</span>
                    Violations of FMCSA regulations or other applicable laws
                  </li>
                  <li className="flex gap-2">
                    <span className="text-orange-500 font-bold">•</span>
                    Accidents, injuries, or property damage occurring during transportation
                  </li>
                  <li className="flex gap-2">
                    <span className="text-orange-500 font-bold">•</span>
                    Breach of this agreement or misrepresentation of qualifications
                  </li>
                  <li className="flex gap-2">
                    <span className="text-orange-500 font-bold">•</span>
                    Failure to maintain required licenses, insurance, or certifications
                  </li>
                </ul>
                <p className="text-slate-700 mt-4">
                  <strong>True Choice Enterprise acts solely as a technology platform connecting customers with carriers and 
                  does not assume responsibility for the actions of carriers or drivers.</strong>
                </p>
              </div>
            </section>

            {/* Section 6 */}
            <section>
              <h2 className="text-xl font-bold text-slate-900 mb-4 flex items-center gap-2">
                <span className="w-8 h-8 bg-orange-500 text-white rounded-full flex items-center justify-center text-sm font-bold">6</span>
                Acknowledgment
              </h2>
              <div className="bg-green-50 border border-green-200 rounded-xl p-6">
                <p className="text-slate-700">
                  By checking the agreement box during registration and using the True Choice Enterprise platform, 
                  I acknowledge that I have read, understood, and agree to comply with all terms of this User Agreement, 
                  including all FMCSA compliance requirements. I understand that failure to comply may result in 
                  suspension or termination of my account and may be reported to the appropriate authorities.
                </p>
              </div>
            </section>

            {/* Contact Information */}
            <section className="border-t border-slate-200 pt-8">
              <h2 className="text-lg font-bold text-slate-900 mb-4">Questions or Concerns?</h2>
              <p className="text-slate-600">
                If you have questions about this agreement or FMCSA compliance requirements, please contact us at:
              </p>
              <div className="mt-4 bg-slate-50 rounded-xl p-4">
                <p className="text-slate-700"><strong>Email:</strong> compliance@truechoiceenterprise.com</p>
                <p className="text-slate-700"><strong>FMCSA Resources:</strong>{' '}
                  <a href="https://www.fmcsa.dot.gov" target="_blank" rel="noopener noreferrer" className="text-orange-500 hover:text-orange-600">
                    www.fmcsa.dot.gov
                  </a>
                </p>
              </div>
            </section>
          </div>
        </div>

        {/* Back Button */}
        <div className="mt-8 text-center">
          <Link to="/auth" className="inline-flex items-center gap-2 text-slate-600 hover:text-slate-900">
            <ArrowLeft className="h-4 w-4" /> Back to Registration
          </Link>
        </div>
      </div>
    </div>
  );
}
